import HelpOutlineOutlinedIcon from "@mui/icons-material/HelpOutlineOutlined";
import { IconButton, Tooltip } from "@mui/material";

import { useTourLauncher } from "./useTourLauncher";

/**
 * One button, one action: start the tour for wherever you are.
 *
 * It used to open a menu with three entries, sitting in the left rail among
 * the navigation. Two of those entries were not really navigation at all:
 * keyboard shortcuts and resetting onboarding are settings, and they now live
 * in the settings dialog. What is left is the thing an author actually reaches
 * for, so it does not need a menu in front of it.
 *
 * Mounted top right on the dashboard header and the 3D editor actionbar. The
 * 360 editor puts the same action in its rail instead, above Exit Editor —
 * see TrainingEditor360Rail. Both share useTourLauncher.
 */

export interface PiHelpButtonProps {
  id?: string;
  testId?: string;
  /** Extra classes for the surface it sits on. */
  className?: string;
}

export default function PiHelpButton({
  id = "onboarding-help-button",
  testId = "onboarding-help-button",
  className,
}: PiHelpButtonProps) {
  const { label, canStart, startTour } = useTourLauncher();

  return (
    <Tooltip title={label} placement="bottom" arrow>
      {/* A disabled button is inert, so the tooltip needs a live wrapper. */}
      <span className="inline-flex">
        <IconButton
          id={id}
          data-testid={testId}
          aria-label={label}
          onClick={startTour}
          disabled={!canStart}
          size="small"
          className={className}
          sx={{
            color: "var(--pi-menu-text-color-1)",
            ":hover": {
              color: "var(--pi-key-blue)",
              backgroundColor: "var(--pi-key-fade)",
            },
          }}
        >
          <HelpOutlineOutlinedIcon fontSize="small" />
        </IconButton>
      </span>
    </Tooltip>
  );
}
